import React, { type FocusEventHandler, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { InfoIcon } from '@pkg/assets/floorGame';
import { NumberCounter, type NumberCounterProps } from '@pkg/components';
import { type CountdownCallback, useCountdown } from '@pkg/hooks';
import { type UtcMilliseconds } from '@pkg/types';
import { flipNumberSign, msTo24h, type Time24hData } from '@pkg/utils';

import { Clock, ClockCount, ClockItem, ClockLabel, Information, Interior, Label, StyledDeathGame } from './styles';

type CommonNumberCounterProps = Pick<NumberCounterProps, 'compact' | 'monospace' | 'leftPad'>;

export interface DeathGameProps {
    timestamp?: UtcMilliseconds;
    compact?: boolean;
    onBlur?: FocusEventHandler;
    onFocus?: FocusEventHandler;
}

const INITIAL_TIME_AGO: Time24hData = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
};

export function DeathGame({ timestamp = 0, compact = false, onBlur, onFocus }: DeathGameProps) {
    const { t: translate } = useTranslation('floorgame');

    const [timeAgo, setTimeAgo] = useState<Time24hData>(INITIAL_TIME_AGO);

    const handleCountdownUpdate: CountdownCallback = async (difference) => {
        // The `useCountdown` hook expects a countdown to `0` (counting down),
        // where as we are calculating "time since" (counting up).
        // So, we simply flip the sign before parsing it.
        const timeSince = flipNumberSign(difference);
        setTimeAgo(msTo24h(timeSince));
    };

    useCountdown(handleCountdownUpdate, timestamp);

    const commonNumberCounterProps: CommonNumberCounterProps = {
        compact,
        monospace: true,
        leftPad: 2,
    };

    return (
        <StyledDeathGame $compact={compact}>
            <Information onBlur={onBlur} onFocus={onFocus}>
                <InfoIcon />
            </Information>

            <Interior>
                <Label>{translate('death_game_label')}</Label>

                <Clock>
                    <ClockItem>
                        <ClockCount>
                            <NumberCounter value={timeAgo.days} {...commonNumberCounterProps} />
                        </ClockCount>
                        <ClockLabel>{translate('death_game_days')}</ClockLabel>
                    </ClockItem>

                    <ClockItem>
                        <ClockCount>
                            <NumberCounter value={timeAgo.hours} {...commonNumberCounterProps} />
                        </ClockCount>
                        <ClockLabel>{translate('death_game_hours')}</ClockLabel>
                    </ClockItem>

                    <ClockItem>
                        <ClockCount>
                            <NumberCounter value={timeAgo.minutes} {...commonNumberCounterProps} />
                        </ClockCount>
                        <ClockLabel>{translate('death_game_minutes')}</ClockLabel>
                    </ClockItem>

                    <ClockItem>
                        <ClockCount>
                            <NumberCounter value={timeAgo.seconds} {...commonNumberCounterProps} />
                        </ClockCount>
                        <ClockLabel>{translate('death_game_seconds')}</ClockLabel>
                    </ClockItem>
                </Clock>
            </Interior>
        </StyledDeathGame>
    );
}
